import React, { useRef, useEffect, useState, useMemo, useImperativeHandle, forwardRef } from "react";
import styled, { keyframes } from "styled-components";
import { DeviceSize } from "constants/deviceSize";
import WaveSurfer from "wavesurfer.js";
import { Track, loadDb } from "../data/schema";
import PlayIcon from "../../../assets/play-player.svg";
import LikeIcon from "../../../assets/like.svg";
import DislikeIcon from "../../../assets/dislike.svg";
import ExportIcon from "../../../assets/export.svg";
import MoreIcon from "../../../assets/more.svg";
import ShuffleIcon from "../../../assets/shuffle.svg";
import NextIcon from "../../../assets/next.svg";
import PreviousIcon from "../../../assets/previous.svg";
import RepeatIcon from "../../../assets/repeate-music.svg";
import VolumeIcon from "../../../assets/volume-high.svg";
import { setTrackReaction } from "../data/schema";
import { initializeQueue, getQueue, setCurrentTrackIndex as setQueueTrackIndex, subscribe as subscribeQueue, clearQueue } from "../data/queue";
import UserStore from "../../../stories/UserStore";
import { observer } from "mobx-react-lite";
import { showToast } from "components/ui-kit/Toast";
import { TrackMenu } from "./TrackMenu";
import { ShareModal } from "./Modals/ShareModal";

type Props = {
  track: Track | null;
  onClose: () => void;
  onTrackChange?: (track: Track) => void;
  onTogglePlay?: (isPlaying: boolean) => void;
  sourceTracks?: Track[]; // Optional: tracks to use for queue initialization
};

export type MusicPlayerHandle = {
  togglePlay: () => void;
};

const audioCache = new Map<string, { objectUrl: string; fetchedAt: number }>();
const audioFetchPromises = new Map<string, Promise<string>>();
const AUDIO_CACHE_MAX_ITEMS = 25;
const loadStartEventName = 'trackAudioLoadStart';
const loadEndEventName = 'trackAudioLoadEnd';

const addGlobalLoadingTrack = (trackId: string) => {
  if (typeof window === 'undefined') return;
  const globalSetKey = '__audioLoadingTrackIds';
  const anyWindow = window as any;
  if (!anyWindow[globalSetKey]) {
    anyWindow[globalSetKey] = new Set<string>();
  }
  anyWindow[globalSetKey].add(trackId);
};

const removeGlobalLoadingTrack = (trackId: string) => {
  if (typeof window === 'undefined') return;
  const anyWindow = window as any;
  const globalSet: Set<string> | undefined = anyWindow.__audioLoadingTrackIds;
  if (globalSet) {
    globalSet.delete(trackId);
  }
};

const getCachedAudioUrl = async (originalUrl: string): Promise<string> => {
  if (audioCache.has(originalUrl)) {
    console.debug('[MusicPlayer] Audio cache hit', { originalUrl });
    return audioCache.get(originalUrl)!.objectUrl;
  }

  if (audioFetchPromises.has(originalUrl)) {
    console.debug('[MusicPlayer] Awaiting in-flight fetch', { originalUrl });
    return audioFetchPromises.get(originalUrl)!;
  }

  const fetchPromise = fetch(originalUrl, { mode: 'cors', credentials: 'omit' })
    .then(async (response) => {
      if (!response.ok) {
        throw new Error(`Failed to fetch audio (${response.status})`);
      }
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);

      audioCache.set(originalUrl, { objectUrl, fetchedAt: Date.now() });
      if (audioCache.size > AUDIO_CACHE_MAX_ITEMS) {
        const oldestEntry = Array.from(audioCache.entries()).sort((a, b) => a[1].fetchedAt - b[1].fetchedAt)[0];
        if (oldestEntry) {
          console.debug('[MusicPlayer] Releasing cached audio URL', { originalUrl: oldestEntry[0] });
          URL.revokeObjectURL(oldestEntry[1].objectUrl);
          audioCache.delete(oldestEntry[0]);
        }
      }

      return objectUrl;
    })
    .catch((error) => {
      audioCache.delete(originalUrl);
      console.error('[MusicPlayer] Failed to cache audio', { originalUrl, error });
      throw error;
    })
    .finally(() => {
      audioFetchPromises.delete(originalUrl);
    });

  audioFetchPromises.set(originalUrl, fetchPromise);
  return fetchPromise;
};

const MusicPlayerComponent = forwardRef<MusicPlayerHandle, Props>(({ track, onClose, onTrackChange, onTogglePlay, sourceTracks }, ref) => {
  const waveformRef = useRef<HTMLDivElement | null>(null);
  const wavesurferRef = useRef<WaveSurfer | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoadingTrack, setIsLoadingTrack] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [shuffle, setShuffle] = useState(false);
  const [repeat, setRepeat] = useState(false);
  const [currentTrackIndex, setCurrentTrackIndex] = useState(0);
  const [playlist, setPlaylist] = useState<Track[]>([]);
  const [reaction, setReaction] = useState<'like' | 'dislike' | null>(track?.reaction || null);
  const [volume, setVolume] = useState(100);
  const [showVolumePopup, setShowVolumePopup] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [submenuOpen, setSubmenuOpen] = useState<'share' | 'download' | 'report' | null>(null);
  const [shareOpen, setShareOpen] = useState(false);
  
  // Update reaction when track changes - load from database to ensure sync
  useEffect(() => {
    if (!track) {
      setReaction(null);
      return;
    }
    
    // Load fresh data from database to ensure sync
    const db = loadDb();
    const dbTrack = db.tracks[track.id];
    if (dbTrack) {
      setReaction(dbTrack.reaction || null);
    } else {
      setReaction(track.reaction || null);
    }
  }, [track?.id]);
  
  // Listen for reaction changes from WorkspacesView
  useEffect(() => {
    if (!track) return;
    
    const handleReactionChanged = (e: CustomEvent) => {
      if (e.detail.trackId === track.id) {
        setReaction(e.detail.reaction);
      }
    };
    
    window.addEventListener('trackReactionChanged', handleReactionChanged as EventListener);
    return () => window.removeEventListener('trackReactionChanged', handleReactionChanged as EventListener);
  }, [track?.id]);
  
  // Use refs to access current values in event handlers
  const repeatRef = useRef(repeat);
  const shuffleRef = useRef(shuffle);
  const playlistRef = useRef(playlist);
  const currentTrackIndexRef = useRef(currentTrackIndex);
  const onTrackChangeRef = useRef(onTrackChange);
  
  // Update refs when values change
  useEffect(() => {
    repeatRef.current = repeat;
  }, [repeat]);
  
  useEffect(() => {
    shuffleRef.current = shuffle;
  }, [shuffle]);
  
  useEffect(() => {
    playlistRef.current = playlist;
  }, [playlist]);
  
  useEffect(() => {
    currentTrackIndexRef.current = currentTrackIndex;
  }, [currentTrackIndex]);
  
  useEffect(() => {
    onTrackChangeRef.current = onTrackChange;
  }, [onTrackChange]);

  // Subscribe to queue changes
  useEffect(() => {
    const unsubscribe = subscribeQueue(() => {
      const queue = getQueue();
      if (queue) {
        setPlaylist(queue.tracks);
        setCurrentTrackIndex(queue.currentTrackIndex);
        // Update track if it changed
        const currentTrack = queue.tracks[queue.currentTrackIndex];
        if (currentTrack && onTrackChange && currentTrack.id !== track?.id) {
          onTrackChange(currentTrack);
        }
      }
    });
    return unsubscribe;
  }, [track?.id, onTrackChange]);

  // Build playlist from sourceTracks or workspace/playlist when track changes
  useEffect(() => {
    if (!track) return;
    const isLocalFile = Boolean((track as any).params?.isLocalFile);
    if (isLocalFile) {
      // For local file playback, do not use global queue; just a single-track playlist
      clearQueue();
      setPlaylist([track]);
      setCurrentTrackIndex(0);
      return;
    }
    
    const queue = getQueue();
    // If queue exists and contains this track, use it
    if (queue && queue.tracks.some(t => t.id === track.id)) {
      const index = queue.tracks.findIndex(t => t.id === track.id);
      if (index >= 0) {
        setQueueTrackIndex(index);
        setPlaylist(queue.tracks);
        setCurrentTrackIndex(index);
      }
      return;
    }
    
    // If sourceTracks is provided, use it (from Library or WorkspacesView)
    if (sourceTracks && sourceTracks.length > 0) {
      // Filter out trashed tracks
      const validTracks = sourceTracks.filter(t => !t.trashed);
      
      // Find current track index
      const index = validTracks.findIndex(t => t.id === track.id);
      const trackIndex = index >= 0 ? index : 0;
      
      // Initialize queue with source tracks
      initializeQueue(validTracks, trackIndex);
      setPlaylist(validTracks);
      setCurrentTrackIndex(trackIndex);
      return;
    }
    
    // Otherwise, initialize queue from workspace/playlist (fallback)
    const db = loadDb();
    // Find workspace containing this track
    const workspace = Object.values(db.workspaces).find(ws => 
      ws.trackIds.includes(track.id)
    );
    
    if (workspace) {
      // Get all tracks from workspace in order
      const tracks = workspace.trackIds
        .map(id => db.tracks[id])
        .filter(Boolean)
        .filter(t => !t.trashed) as Track[];
      
      // Find current track index
      const index = tracks.findIndex(t => t.id === track.id);
      const trackIndex = index >= 0 ? index : 0;
      
      // Initialize queue
      initializeQueue(tracks, trackIndex);
      setPlaylist(tracks);
      setCurrentTrackIndex(trackIndex);
    } else {
      // Check if track is in a playlist
      const playlist = Object.values(db.playlists).find(p => 
        p.trackIds.includes(track.id)
      );
      
      if (playlist) {
        const tracks = playlist.trackIds
          .map(id => db.tracks[id])
          .filter(Boolean)
          .filter(t => !t.trashed) as Track[];
        
        const index = tracks.findIndex(t => t.id === track.id);
        const trackIndex = index >= 0 ? index : 0;
        
        initializeQueue(tracks, trackIndex);
        setPlaylist(tracks);
        setCurrentTrackIndex(trackIndex);
      } else {
        // Single track, no workspace/playlist
        initializeQueue([track], 0);
        setPlaylist([track]);
        setCurrentTrackIndex(0);
      }
    }
  }, [track?.id, sourceTracks]);

  // Load track when it changes
  useEffect(() => {
    if (!track) return;
    
    const cleanup = loadTrack(track);
    return cleanup;
  }, [track?.id]);
  
  // Update volume when it changes
  useEffect(() => {
    if (wavesurferRef.current) {
      wavesurferRef.current.setVolume(volume / 100);
    }
  }, [volume]);
  
  // Close volume popup when clicking outside
  useEffect(() => {
    if (!showVolumePopup) return;
    
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('[data-volume-popup]')) {
        setShowVolumePopup(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showVolumePopup]);

  // Function to load a track
  const loadTrack = (trackToLoad: Track) => {
    const playableUrl = trackToLoad?.params?.localAudioUrl || null;
    console.debug('[MusicPlayer] loadTrack invoked', {
      trackId: trackToLoad.id,
      title: trackToLoad.title,
      playableUrl,
    });
    setIsPlaying(false);
    setIsLoadingTrack(true);
    setDuration(0);
    setCurrentTime(0);
    if (!waveformRef.current || !playableUrl) {
      console.warn('[MusicPlayer] Missing waveform container or playable URL', {
        hasContainer: Boolean(waveformRef.current),
        playableUrl,
      });
      setIsLoadingTrack(false);
      showToast("Audio source is not available for this track.", undefined, "error");
      return;
    }
    
    // Clean up previous instance
    if (wavesurferRef.current) {
      wavesurferRef.current.destroy();
      wavesurferRef.current = null;
    }

    const wavesurfer = WaveSurfer.create({
      container: waveformRef.current,
      waveColor: "#e9ecef",
      progressColor: "#fff",
      height: 10,
      barWidth: 2,
      normalize: true,
    });
    let hasBeenDestroyed = false;
    
    let audioUrl = playableUrl;
    
    if (typeof audioUrl !== 'string') {
      setIsLoadingTrack(false);
      showToast("Audio source format is invalid.", undefined, "error");
      return;
    }
    
    let cancelled = false;
    let loadStartDispatched = false;
    let loadEndDispatched = false;

    const dispatchLoadStart = () => {
      if (loadStartDispatched) return;
      loadStartDispatched = true;
      loadEndDispatched = false;
      addGlobalLoadingTrack(trackToLoad.id);
      window.dispatchEvent(
        new CustomEvent(loadStartEventName, {
          detail: { trackId: trackToLoad.id },
        })
      );
    };

    const dispatchLoadEnd = () => {
      if (!loadStartDispatched || loadEndDispatched) return;
      loadEndDispatched = true;
      removeGlobalLoadingTrack(trackToLoad.id);
      window.dispatchEvent(
        new CustomEvent(loadEndEventName, {
          detail: { trackId: trackToLoad.id },
        })
      );
    };

    const loadAudioIntoWaveSurfer = async () => {
      try {
        let sourceUrl = audioUrl;
        const shouldCache = false; // caching temporarily disabled
        if (shouldCache) {
          try {
            sourceUrl = await getCachedAudioUrl(audioUrl);
          } catch (error) {
            console.warn('[MusicPlayer] Falling back to original URL after cache failure', { audioUrl, error });
            sourceUrl = audioUrl;
          }
        }

        if (cancelled) {
          if (shouldCache && sourceUrl.startsWith('blob:') && !audioCache.has(audioUrl)) {
            URL.revokeObjectURL(sourceUrl);
          }
          return;
        }

        wavesurfer.load(sourceUrl);
        console.debug('[MusicPlayer] WaveSurfer loading source', { originalUrl: audioUrl, sourceUrl });
      } catch (error) {
        console.error('[MusicPlayer] Failed to load audio', { audioUrl, error });
        setIsLoadingTrack(false);
        showToast("Failed to load audio source.", undefined, "error");
        wavesurfer.destroy();
        dispatchLoadEnd();
        hasBeenDestroyed = true;
        if (wavesurferRef.current === wavesurfer) {
          wavesurferRef.current = null;
        }
      }
    };

    dispatchLoadStart();
    loadAudioIntoWaveSurfer();
    wavesurferRef.current = wavesurfer;
    dispatchLoadStart();

    wavesurfer.on("ready", () => {
      setIsLoadingTrack(false);
      dispatchLoadEnd();
      setDuration(wavesurfer.getDuration());
      // Set initial volume
      wavesurfer.setVolume(volume / 100);
      wavesurfer.play().catch((error) => {
        console.error("[MusicPlayer] Auto-play failed", error);
        showToast("Unable to start playback automatically.", undefined, "error");
        setIsPlaying(false);
      });
    });
    wavesurfer.on("error", (err) => {
      console.error("[MusicPlayer] WaveSurfer error", err);
      setIsLoadingTrack(false);
      setIsPlaying(false);
      showToast("Audio playback error. Try another track.", undefined, "error");
      wavesurfer.destroy();
      dispatchLoadEnd();
      hasBeenDestroyed = true;
      if (wavesurferRef.current === wavesurfer) {
        wavesurferRef.current = null;
      }
    });

    wavesurfer.on("play", () => {
      setIsPlaying(true);
      if (onTogglePlay) onTogglePlay(true);
    });
    wavesurfer.on("pause", () => {
      setIsPlaying(false);
      if (onTogglePlay) onTogglePlay(false);
    });
    
    wavesurfer.on("finish", () => {
      setIsPlaying(false);
      if (onTogglePlay) onTogglePlay(false);
      // Use refs to access current values
      if (!repeatRef.current) {
        // Move to next track
        const currentPlaylist = playlistRef.current;
        const currentIndex = currentTrackIndexRef.current;
        if (currentPlaylist.length > 0) {
          let nextIndex: number;
          if (shuffleRef.current) {
            const availableIndices = currentPlaylist
              .map((_, i) => i)
              .filter(i => i !== currentIndex);
            if (availableIndices.length === 0) {
              nextIndex = currentIndex;
            } else {
              nextIndex = availableIndices[Math.floor(Math.random() * availableIndices.length)];
            }
          } else {
            nextIndex = (currentIndex + 1) % currentPlaylist.length;
          }
          
          const nextTrack = currentPlaylist[nextIndex];
          if (nextTrack && onTrackChangeRef.current) {
            setCurrentTrackIndex(nextIndex);
            setQueueTrackIndex(nextIndex);
            onTrackChangeRef.current(nextTrack);
          }
        }
      } else {
        // Repeat current track
        wavesurfer.seekTo(0);
        wavesurfer.play();
      }
    });

    const updateTime = () => {
      if (wavesurferRef.current) {
        const nextTime = wavesurferRef.current.getCurrentTime();
        setCurrentTime(nextTime);
        if ((trackToLoad as any).params?.isLocalFile) {
          window.dispatchEvent(
            new CustomEvent('localReferenceTimeUpdate', {
              detail: {
                localAudioUrl: playableUrl,
                currentTime: nextTime,
              },
            })
          );
        }
      }
    };
    
    const interval = setInterval(updateTime, 100);
    
    return () => {
      clearInterval(interval);
      cancelled = true;
      setIsLoadingTrack(false);
      if (!hasBeenDestroyed) {
        wavesurfer.destroy();
      }
      if (wavesurferRef.current === wavesurfer) {
        wavesurferRef.current = null;
      }
      dispatchLoadEnd();
    };
  };

  const handleNext = () => {
    if (playlist.length === 0) return;
    
    let nextIndex: number;
    if (shuffle) {
      // Random track, but not the current one
      const availableIndices = playlist
        .map((_, i) => i)
        .filter(i => i !== currentTrackIndex);
      if (availableIndices.length === 0) {
        nextIndex = currentTrackIndex;
      } else {
        nextIndex = availableIndices[Math.floor(Math.random() * availableIndices.length)];
      }
    } else {
      // Next track in order
      nextIndex = (currentTrackIndex + 1) % playlist.length;
    }
    
    const nextTrack = playlist[nextIndex];
    if (nextTrack && onTrackChange) {
      setCurrentTrackIndex(nextIndex);
      setQueueTrackIndex(nextIndex);
      onTrackChange(nextTrack);
    }
  };

  const handlePrevious = () => {
    if (playlist.length === 0) return;
    
    let prevIndex: number;
    if (shuffle) {
      // Random track, but not the current one
      const availableIndices = playlist
        .map((_, i) => i)
        .filter(i => i !== currentTrackIndex);
      if (availableIndices.length === 0) {
        prevIndex = currentTrackIndex;
      } else {
        prevIndex = availableIndices[Math.floor(Math.random() * availableIndices.length)];
      }
    } else {
      // Previous track in order
      prevIndex = currentTrackIndex === 0 ? playlist.length - 1 : currentTrackIndex - 1;
    }
    
    const prevTrack = playlist[prevIndex];
    if (prevTrack && onTrackChange) {
      setCurrentTrackIndex(prevIndex);
      setQueueTrackIndex(prevIndex);
      onTrackChange(prevTrack);
    }
  };

  const togglePlay = () => {
    if (isLoadingTrack) {
      return;
    }
    if (!wavesurferRef.current) {
      console.warn("[MusicPlayer] togglePlay called without audio instance");
      showToast("Audio is not ready yet.", undefined, "error");
      return;
    }
    wavesurferRef.current.playPause();
  };
  
  useImperativeHandle(ref, () => ({
    togglePlay,
  }));

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handleReaction = async (type: 'like' | 'dislike') => {
    if (!track) return;
    const next = reaction === type ? null : type;
    setReaction(next);
    try {
      await setTrackReaction(track.id, next);
      // Dispatch event to notify WorkspacesView about reaction change
      window.dispatchEvent(new CustomEvent('trackReactionChanged', { 
        detail: { trackId: track.id, reaction: next } 
      }));
    } catch (error) {
      console.error('Failed to set track reaction:', error);
      showToast('Failed to update reaction', undefined, 'error');
      // Revert reaction on error
      setReaction(reaction);
    }
  };

  const handleVolumeChange = (newVolume: number) => {
    const clamped = Math.max(0, Math.min(100, newVolume));
    setVolume(clamped);
    if (wavesurferRef.current) {
      wavesurferRef.current.setVolume(clamped / 100);
    }
  };
  
  const volumeSliderRef = useRef<HTMLDivElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  
  const handleVolumeMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    setIsDragging(true);
    handleVolumeMouseMove(e);
  };
  
  const handleVolumeMouseMove = (e: React.MouseEvent<HTMLDivElement> | MouseEvent) => {
    if (!volumeSliderRef.current) return;
    const rect = volumeSliderRef.current.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const percentage = Math.max(0, Math.min(100, 100 - (y / rect.height) * 100));
    handleVolumeChange(percentage);
  };
  
  useEffect(() => {
    if (!isDragging) return;
    
    const handleMouseMove = (e: MouseEvent) => handleVolumeMouseMove(e);
    const handleMouseUp = () => setIsDragging(false);
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  const isLocalFile = Boolean(track?.params && (track as any).params?.isLocalFile);

  const artworkUrl =
    track?.params?.localImageUrl ||
    track?.params?.imageUrl ||
    null;

  if (!track) return null;

  return (
    <PlayerContainer>
      <CloseButton onClick={onClose}>×</CloseButton>
      <LeftSection>
        <AlbumArt>
          <ArtworkImage $imageUrl={artworkUrl} />
        </AlbumArt>
        <TrackInfo>
          <TrackTitle>{track.title || 'Song'}</TrackTitle>
          {!isLocalFile && (
            <ArtistName>
              {UserStore.user ? `${UserStore.user.firstname} ${UserStore.user.lastname}`.trim() : 'User'}
            </ArtistName>
          )}
          <TimeInfo>
            {formatTime(currentTime)} / {formatTime(duration)}
          </TimeInfo>
        </TrackInfo>
      </LeftSection>
      <WaveformContainer ref={waveformRef} />
      <ProgressBarContainer
        onClick={(e) => {
          if (!wavesurferRef.current || !duration) return;
          
          const rect = e.currentTarget.getBoundingClientRect();
          const clickX = e.clientX - rect.left;
          const percentage = clickX / rect.width;
          const newTime = Math.max(0, Math.min(1, percentage)) * duration;
          
          wavesurferRef.current.seekTo(percentage);
          setCurrentTime(newTime);
        }}
      >
        <ProgressBar
          style={{ width: `${duration ? (currentTime / duration) * 100 : 0}%` }}
        />
      </ProgressBarContainer>
      <CenterSection>
        <ControlButton
          $active={shuffle && !isLocalFile}
          $disabled={isLocalFile}
          onClick={() => {
            if (isLocalFile) return;
            setShuffle(!shuffle);
          }}
        >
          <ShuffleIcon />
        </ControlButton>
        <ControlButton
          $disabled={isLocalFile}
          onClick={() => {
            if (isLocalFile) return;
            handleNext();
          }}
        >
          <PreviousIcon />
        </ControlButton>
        <PlayButton onClick={togglePlay} disabled={isLoadingTrack}>
          {isLoadingTrack ? (
            <LoadingSpinner />
          ) : isPlaying ? (
            <svg viewBox="0 0 24 24" style={{ width: '20px', height: '20px', fill:'#6777FF'  }}>
              <rect x="6" y="4" width="4" height="16" fill='#6777FF' />
              <rect x="14" y="4" width="4" height="16" fill='#6777FF' />
            </svg>
          ) : (
            <PlayIcon />
          )}
        </PlayButton>
        <ControlButton
          $disabled={isLocalFile}
          onClick={() => {
            if (isLocalFile) return;
            handlePrevious();
          }}
        >
          <NextIcon />
        </ControlButton>
        <ControlButton
          $active={repeat && !isLocalFile}
          $disabled={isLocalFile}
          onClick={() => {
            if (isLocalFile) return;
            setRepeat(!repeat);
          }}
        >
          <RepeatIcon />
        </ControlButton>
      </CenterSection>
      <RightSection>
        {!isLocalFile && (
          <>
            <ReactionButton
              $active={reaction === 'like'}
              onClick={() => handleReaction('like')}
            >
              <LikeIcon />
            </ReactionButton>
            <ReactionButton
              $active={reaction === 'dislike'}
              onClick={() => handleReaction('dislike')}
            >
              <DislikeIcon />
            </ReactionButton>
          </>
        )}
        <VolumeButtonWrapper data-volume-popup>
          <ActionButton onClick={() => setShowVolumePopup(!showVolumePopup)}>
            <VolumeIcon />
          </ActionButton>
          {showVolumePopup && (
            <VolumePopup onClick={(e) => e.stopPropagation()}>
              <VolumeSliderWrapper
                ref={volumeSliderRef}
                onMouseDown={handleVolumeMouseDown}
              >
                <VolumeTrack>
                  <VolumeFill $isDragging={isDragging} style={{ height: `${volume}%` }} />
                  <VolumeThumb $isDragging={isDragging} style={{ bottom: `${volume}%` }} />
                </VolumeTrack>
              </VolumeSliderWrapper>
            </VolumePopup>
          )}
        </VolumeButtonWrapper>
        {!isLocalFile && (
          <PlayerMoreWrapper data-player-menu="true" onClick={(e) => e.stopPropagation()}>
            <ActionButton
              title="More"
              onClick={(e) => {
                e.stopPropagation();
                if (!track) return;
                setMenuOpen((prev) => !prev);
                setSubmenuOpen(null);
              }}
            >
              <MoreIcon />
            </ActionButton>
            {menuOpen && track && (
              <TrackMenu
                trackId={track.id}
                track={track}
                onClose={() => {
                  setMenuOpen(false);
                  setSubmenuOpen(null);
                }}
                submenuOpen={submenuOpen}
                onSubmenuOpen={setSubmenuOpen}
              />
            )}
          </PlayerMoreWrapper>
        )}
      </RightSection>
      <ShareModal
        open={shareOpen && !!track}
        track={track || undefined}
        onClose={() => setShareOpen(false)}
      />
    </PlayerContainer>
  );
});

export const MusicPlayer = observer(MusicPlayerComponent);

const PlayerContainer = styled.div`
  position: absolute;
  bottom: 10px;
  left: 10px;
  right: 10px;
  height: 100px;
  border-radius: 20px;
  padding: 12px 20px;
  display: flex;
  align-items: center;
  gap: 20px;
  /* ниже сайдбара (z-index: 40 в MainLayout.SidebarOverlay), но выше контента */
  z-index: 30;
  padding-bottom: 30px;

  @media (max-width: ${DeviceSize.TabletMedium}) {
    padding-bottom: 0;
    max-height: auto;
  }
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: #fff;
  border-radius: 20px;
    z-index: 0;
  }
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: #6777FF;
    opacity: 0.3;
  border-radius: 20px;
    z-index: 1;
    pointer-events: none;
  }
  > * {
    position: relative;
    z-index: 2;
  }

  @media (max-width: ${DeviceSize.TabletMedium}) {
    height: auto;
    padding: 10px 12px 18px;
    gap: 12px;
    flex-direction: column;
    align-items: stretch;
  }
`;

const PlayerMoreWrapper = styled.div`
  position: relative;
  display: inline-block;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 5px;
  right: 8px;
  width: 24px;
  height: 24px;
  border-radius: 12px;
  background: transparent;
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.3);
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  line-height: 1;
  transition: all 0.2s ease;
  z-index: 3;
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.5);
  }
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 250px;

  @media (max-width: ${DeviceSize.TabletMedium}) {
    min-width: 0;
    width: 100%;
    gap: 10px;
  }
`;

const AlbumArt = styled.div`
  width: 40px;
  height: 60px;
  aspect-ratio: 3 / 4;
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;

  @media (max-width: ${DeviceSize.TabletMedium}) {
    width: 56px;
    height: 56px;
    aspect-ratio: 1 / 1;
    border-radius: 12px;
  }
`;

const ArtworkImage = styled.div<{ $imageUrl?: string }>`
  width: 100%;
  height: 100%;
  background: ${({ $imageUrl }) =>
    $imageUrl
      ? `url(${$imageUrl}) center center / cover no-repeat`
      : 'linear-gradient(135deg, #e8eaff, #f4f4f4)'};
`;

const TrackInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  min-width: 0;

  @media (max-width: ${DeviceSize.TabletMedium}) {
    align-items: center;
  }
`;

const TrackTitle = styled.div`
  font-weight: 600;
  color: #000;
  font-size: 14px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  flex: 1;
  min-width: 0;
`;

const ArtistName = styled.div`
  color: #999;
  font-size: 12px;
`;

const TimeInfo = styled.div`
  color: #999;
  font-size: 11px;

  flex: 0 0 auto;
  white-space: nowrap;
`;

const WaveformContainer = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 10px;
  z-index: 1;
  border-radius: 0 0 20px 20px;
  overflow: hidden;
`;

const ProgressBarContainer = styled.div`
  position: absolute;
  bottom: 0;
  left: 0px;
  right: 0px;
  height: 20px;
  background: #6777FF;
  border: 1px solid #6777FF;
  border-radius: 5px 5px 40px 40px;
  z-index: 2;
  overflow: hidden;
  cursor: pointer;
`;

const ProgressBar = styled.div`
  height: 100%;
  background: #fff;
  transition: width 0.1s linear;
  border-radius: 0 0 0 20px;
  box-shadow: 0 0 8px rgba(255, 255, 255, 0.5);
`;

const CenterSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  justify-content: center;
`;

const ControlButton = styled.button<{ $active?: boolean; $disabled?: boolean }>`
  background: ${p => p.$active ? '#fff' : 'transparent'};
  border: none;
  color: ${p => p.$active ? '#6777FF' : '#fff'};
  font-size: 18px;
  cursor: ${p => (p.$disabled ? 'not-allowed' : 'pointer')};
  padding: 8px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${p => (p.$disabled ? 0.4 : 1)};
  transition: all 0.2s ease;
  &:hover {
    opacity: ${p => (p.$disabled ? 0.4 : 1)};
  }
  svg {
    width: 20px;
    height: 20px;
    fill: currentColor;
  }
  &:first-child svg,
  &:last-child svg {
    width: 13px;
    height: 13px;
  }
`;

const PlayButton = styled.button<{ disabled?: boolean }>`
  background: transparent;
  border: none;
  color: #fff;
  font-size: 24px;
  cursor: ${p => (p.disabled ? "not-allowed" : "pointer")};
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${p => (p.disabled ? 0.6 : 1)};
  svg {
    width: 20px;
    height: 20px;
    fill: currentColor;
  }
`;

const spinnerRotation = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const LoadingSpinner = styled.div`
  width: 18px;
  height: 18px;
  border: 3px solid rgba(103, 119, 255, 0.25);
  border-top-color: #6777FF;
  border-radius: 50%;
  animation: ${spinnerRotation} 0.9s linear infinite;
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 200px;
  justify-content: flex-end;
  margin-right: 10px;

  @media (max-width: ${DeviceSize.TabletMedium}) {
    display: none;
  }
`;

const ActionButton = styled.button`
  background: transparent;
  border: none;
  color: #6777FF;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.7;
  transition: opacity 0.2s ease;
  &:hover {
    opacity: 1;
  }
  svg {
    width: 20px;
    height: 20px;
    fill: currentColor;
  }
  svg * {
    fill: currentColor;
  }
`;

const ReactionButton = styled.button<{ $active?: boolean }>`
  background: transparent;
  border: none;
  color: ${p => p.$active ? '#6777FF' : '#9aa0a6'};
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.7;
  transition: color 0.2s ease;
  &:hover {
    opacity: 1;
    color: #6777FF;
  }
  svg {
    width: 20px;
    height: 20px;
    fill: currentColor;
  }
  svg * {
    fill: currentColor;
  }
`;

const VolumeButtonWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const VolumePopup = styled.div`
  position: absolute;
  bottom: calc(100% + 12px);
  right: -10px;
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 16px 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  box-shadow: 0 10px 24px rgba(0,0,0,0.12);
  z-index: 100;
  min-width: 50px;
`;

const VolumeSliderWrapper = styled.div`
  position: relative;
  width: 8px;
  height: 120px;
  cursor: pointer;
  user-select: none;
`;

const VolumeTrack = styled.div`
  position: relative;
  width: 8px;
  height: 120px;
  background: #e9ecef;
  border-radius: 4px;
  overflow: visible;
`;

const VolumeFill = styled.div<{ $isDragging?: boolean }>`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: #6777FF;
  border-radius: 4px;
  transition: ${p => p.$isDragging ? 'none' : 'height 0.1s linear'};
  pointer-events: none;
`;

const VolumeThumb = styled.div<{ $isDragging?: boolean }>`
  position: absolute;
  left: 50%;
  transform: translate(-50%, 50%);
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #6777FF;
  border: 2px solid #fff;
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
  pointer-events: none;
  margin-left: 0;
  transition: ${p => p.$isDragging ? 'none' : 'bottom 0.1s linear'};
`;


export default MusicPlayer;

